import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMap } from "react-leaflet";
import { searchHospitals } from '../../../pages/actions/actions';
import { ShowAlert } from '../../../utils/showAlert';

function ResizeMapOnShow() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [map]);
  return null;
}

const customIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const hospitalIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface Hospital {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  handleEmergency?: boolean;
  distance?: number;
}

interface MapViewProps {
  addressSelected: any;
}

const getLatitude = (address: any) => {
  if (address?.location?.latitude) return address.location.latitude;
  if (Array.isArray(address?.location) && address?.location[0]?.latitude) return address.location[0].latitude;
  if (address?.geoLocation?.coordinates) return address.geoLocation.coordinates[0];
  if (address?.location?.coordinates) return address.location.coordinates[1];
  return null;
};

const getLongitude = (address: any) => {
  if (address?.location?.longitude) return address.location.longitude;
  if (Array.isArray(address?.location) && address?.location[0]?.longitude) return address.location[0].longitude;
  if (address?.geoLocation?.coordinates) return address.geoLocation.coordinates[1];
  if (address?.location?.coordinates) return address.location.coordinates[0];
  return null;
};

const fetchCoordinates = (address: any): [number, number] => {
  const latitude = getLatitude(address);
  const longitude = getLongitude(address);
  return [latitude, longitude];
}

export const MapView: React.FC<MapViewProps> = ({ addressSelected }) => {
  const [position, setPosition] = useState<[number, number]>(fetchCoordinates(addressSelected));
  const [loading, setLoading] = useState(true);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);

  const getHospitals = async (payload: {
    latitude: any;
    longitude: any;
    radius: number;
  }) => {
    setLoadingHospitals(true);
    setHospitals([]);
    try {
      const response = await searchHospitals({
        name: "",
        PickupLatitude: payload.latitude,
        PickupLongitude: payload.longitude,
        radius: payload.radius
      });
      if (response) {
        setHospitals(response?.data?.slice(0, 100));
        setLoadingHospitals(false);
      }
    } catch (e: any) {
      console.error('error:', e)
      setLoadingHospitals(false);
      ShowAlert("warning", e.response.data.message || "some issue while getting hospital list")
    }
  }

  useEffect(() => {
    const latitude = getLatitude(addressSelected);
    const longitude = getLongitude(addressSelected);
    if (latitude && longitude) {
      setPosition([latitude, longitude]);
      setLoading(false);
      getHospitals({ longitude, latitude, radius: 20000 });
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setPosition([pos.coords.latitude, pos.coords.longitude]);
            setLoading(false);
            getHospitals({ longitude: pos.coords.longitude, latitude: pos.coords.latitude, radius: 20000 });
          },
          () => setLoading(false)
        );
      } else {
        setLoading(false);
      }
    }
  }, [addressSelected]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading map...</div>;
  }

  return (
    <div style={{ height: '400px', width: '100%' }}>
      {loadingHospitals && (
        <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1000, background: 'white', padding: '5px 10px', borderRadius: '4px', fontSize: '12px' }}>
          Loading hospitals...
        </div>
      )}
      <MapContainer
        center={position}
        zoom={15}
        scrollWheelZoom={false}
        style={{ width: '100%', height: '400px' }}
      >
        <ResizeMapOnShow />
        <TileLayer
          url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=en&gl=US"
          attribution='<a href="http://www.pococare.com">Pococare</a>'
          subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
        />
        <Marker
          position={position}
          icon={customIcon}
          draggable={true}
          eventHandlers={{
            dragend: (e) => {
              const marker = e.target;
              const newPos = marker.getLatLng();
              setPosition([newPos.lat, newPos.lng]);
              getHospitals({ longitude: newPos.lng, latitude: newPos.lat, radius: 20000 });
            }
          }}
        >
          <Popup>
            <div>
              <p style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>
                Drag me to change the location
              </p>
              <strong>{addressSelected?.label || addressSelected?.name || 'Selected Location'}</strong>
              <br />
              {addressSelected?.addressLine1 || addressSelected?.address || 'Current Location'}
            </div>
          </Popup>
        </Marker>

        {hospitals.map((hospital, index) => (
          <Marker
            key={hospital.id || index}
            position={fetchCoordinates(hospital)}
            icon={hospitalIcon}
          >
            <Popup>
              <div>
                <strong style={{ color: 'green' }}>{hospital.name}</strong>
                <br />
                <span>{hospital.address}</span>
                {hospital.distance && (
                  <>
                    <br />
                    <span style={{ fontSize: '12px', color: '#666' }}>Distance: {hospital.distance.toFixed(2)} km</span>
                  </>
                )}
                {hospital.handleEmergency && (
                  <>
                    <br />
                    <span style={{ fontSize: '12px', color: 'red', fontWeight: 'bold' }}>Emergency Services Available</span>
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};